// Module-local IPFS helpers for seometadata. Self-contained — no imports
// from `src/utils/`. Per `vault/how-to/how-to-build-new-module.md`
// (module-local everything rule) and `vault/how-to/ipfs-gateway-pattern.md`
// (hash-only canonical storage).
//
// Pattern:
//   - Entities store CID only (`ogImageCid`, `twitterImageCid`, `linkedInImageCid`,
//     `pinterestImageCid`).
//   - URL is built at render time from `tenant.apiKeys.ipfsGatewayUrl`.
//   - Each module owns its own copy of this util — no shared singleton.
//   - Apollo cache serves as the de-facto singleton: the gateway query is
//     `cache-first`, so the first caller fetches and every subsequent
//     caller (anywhere in the app) hits the cache.

import { gql, useQuery } from "@apollo/client";

const GET_SEOMETADATA_IPFS_GATEWAY_URL = gql`
  query GetSeometadataIpfsGatewayUrl {
    currentTenant {
      id
      apiKeys {
        ipfsGatewayUrl
      }
    }
  }
`;

const CIDV0 = /^Qm[1-9A-HJ-NP-Za-km-z]{44}$/;
const CIDV1 = /^b[A-Za-z2-7]{58,}$/;

/**
 * Cheap CID validator. Accepts CIDv0 (Qm…, 46 chars) and CIDv1 (bafy/bafk/…).
 */
export const isLikelyCid = (value: string | undefined | null): boolean => {
  if (!value || typeof value !== "string") return false;
  return CIDV0.test(value) || CIDV1.test(value);
};

/**
 * Pure URL builder. Pass the gateway URL explicitly (typically from
 * `useIpfsGatewayUrl`). Strips trailing slashes and `/ipfs` suffix on
 * the gateway so callers can pass either shape.
 */
export const buildIpfsUrl = (
  cid: string | undefined | null,
  gatewayUrl: string | undefined | null
): string => {
  if (!cid) return "";
  if (cid.startsWith("http://") || cid.startsWith("https://")) return cid;
  if (!gatewayUrl) return "";
  let base = gatewayUrl.trim();
  while (base.endsWith("/")) base = base.slice(0, -1);
  if (base.endsWith("/ipfs")) base = base.slice(0, -5);
  const cleanCid = cid.startsWith("/") ? cid.slice(1) : cid;
  return `${base}/ipfs/${cleanCid}`;
};

/**
 * Hook — returns the current tenant's IPFS gateway URL (or empty string
 * while loading / if the tenant has no gateway configured).
 *
 * cache-first means the first caller fetches; every subsequent caller in
 * any component hits Apollo cache. No app-level singleton needed.
 */
export const useIpfsGatewayUrl = (): string => {
  const { data } = useQuery(GET_SEOMETADATA_IPFS_GATEWAY_URL, { fetchPolicy: "cache-first" });
  return data?.currentTenant?.apiKeys?.ipfsGatewayUrl || "";
};

/**
 * Hook — `<img src={useImageSrc(cid)} />`. Combines the gateway lookup +
 * builder. For map loops, prefer `useIpfsGatewayUrl` once at the top of
 * the component and call `buildIpfsUrl(cid, gateway)` per item.
 */
export const useImageSrc = (cid: string | undefined | null): string => {
  const gateway = useIpfsGatewayUrl();
  return buildIpfsUrl(cid, gateway);
};
